/**
 * 點數效期顯示規則 —— **全站唯一判準**(R391.2;BE D9,`me_router.py` docstring 原話:
 * 「台北午夜 → 顯示日期即可,否則顯示日期+時分」)。
 * -----------------------------------------------------------------------------
 * 消費端:兌換成功畫面(RedeemCodeDialog)/ admin 清單「點數效期」格(RedeemAdminPage)
 * / 帳號選單到期提示(SidebarAccountMenu)。三處都吃這一支 —— 各自再寫一份判準,
 * 遲早對同一批點數顯示兩種到期時間(R251 族)。
 *
 * 判準:`expireAt`(失效瞬間,UTC)換算台北後 **00:00:00.000 整** = 天級 → 顯示
 * 「有效至」日期(用 BE 給的 `expireOn`;它就是最後可用日,含當天);否則 = 分鐘級
 * → 顯示台北的 M/D + HH:mm(失效那一刻)。⚠️ 秒也要驗 —— 分鐘級的秒數繼承兌換
 * 時刻,可能剛好落在 00:00:xx,只驗時分會把它誤判成天級。
 *
 * fail-soft:`expireAt` 缺席 / 壞字串 → 退 `expireOn` 當天級;兩者皆無 → null
 * (永久點,呼叫端不顯效期)。天級而 `expireOn` 竟缺席 → 由失效瞬間減一天推得
 * (與 types/wallet.ts `lotExpiresOnFromExpiresAt` 同語意)。
 */

const TAIPEI_OFFSET_MS = 8 * 60 * 60 * 1000; // 台北無 DST,固定 UTC+8
const DAY_MS = 24 * 60 * 60 * 1000;

export type PointsExpiryDisplay =
  | {
      /** 天級:顯示「有效至 {date}」即可。 */
      kind: 'date';
      /** 最後可用日(台北,含當天),`YYYY-MM-DD`。 */
      date: string;
    }
  | {
      /** 分鐘級:顯示「{monthDay} {time}」(台北時刻,失效那一刻)。 */
      kind: 'datetime';
      /** 台北日期 `YYYY-MM-DD`(要顯完整日期的呼叫端用)。 */
      date: string;
      /** 台北 `M/D`(不補零;與帳號選單既有格式一致)。 */
      monthDay: string;
      /** 台北 `HH:mm`(補零)。 */
      time: string;
    };

/** `YYYY-MM-DD` → `M/D`(刻意不過 `Date` —— 西半球時區會把日期挪一天)。 */
export function monthDayOf(isoDate: string): string {
  const m = /^\d{4}-(\d{2})-(\d{2})$/.exec(isoDate);
  return m ? `${Number(m[1])}/${Number(m[2])}` : isoDate;
}

export function pointsExpiryDisplay(
  expireAt: string | null | undefined,
  expireOn: string | null | undefined,
): PointsExpiryDisplay | null {
  const on = typeof expireOn === 'string' && expireOn !== '' ? expireOn : null;
  const instantMs = typeof expireAt === 'string' ? Date.parse(expireAt) : Number.NaN;
  if (!Number.isFinite(instantMs)) {
    // 失效瞬間拿不到(舊 BE / 壞值)→ 只能當天級;連日期都沒有 = 永久點,不顯。
    return on ? { kind: 'date', date: on } : null;
  }
  const taipeiMs = instantMs + TAIPEI_OFFSET_MS;
  const isTaipeiMidnight = taipeiMs % DAY_MS === 0;
  if (isTaipeiMidnight) {
    // 天級:失效瞬間 = 最後可用日「隔天」00:00 → 有效至 = 減一天;BE 的 expireOn
    // 就是這個值,優先用它(不重算)。
    const derived = new Date(taipeiMs - DAY_MS).toISOString().slice(0, 10);
    return { kind: 'date', date: on ?? derived };
  }
  const taipei = new Date(taipeiMs);
  const date = taipei.toISOString().slice(0, 10);
  const hh = String(taipei.getUTCHours()).padStart(2, '0');
  const mm = String(taipei.getUTCMinutes()).padStart(2, '0');
  return { kind: 'datetime', date, monthDay: monthDayOf(date), time: `${hh}:${mm}` };
}
